import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import toastr from 'toastr';
import { getToxReviewQueue, batchToxReview } from '../../../services/clinicPortalServices/orderSearchService';
import ToxReviewPopup from '../toxReview/ToxReviewPopup';

const ACTIONS = {
    apply: { label: 'Apply to Report', variant: 'outline-dark', help: 'Writes the tox interpretation to each report and regenerates the PDF.' },
    preliminary: { label: 'Apply & Mark Preliminary', variant: 'outline-dark', help: 'Applies the interpretation if needed, then marks each report Preliminary.' },
    approve: { label: 'Approve to Final', variant: 'success', help: 'Applies the interpretation if needed, then approves each report to Final. Orders with critical values need a logged callback first and will be skipped otherwise.' }
};
const fmtDate = (s) => { s = String(s || ''); return /^\d{8}/.test(s) ? `${s.slice(4, 6)}/${s.slice(6, 8)}/${s.slice(0, 4)}` : s; };
const pill = (bg, fg) => ({ display: 'inline-block', minWidth: 26, padding: '2px 8px', borderRadius: 10, background: bg, color: fg, fontWeight: 600, fontSize: 12, textAlign: 'center' });
const GREY = pill('#e9ecef', '#495057');

export default function ToxReviewQueue() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState({});
    const [onlyExceptions, setOnlyExceptions] = useState(false);
    const [action, setAction] = useState(null);
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [results, setResults] = useState(null);
    const [reviewId, setReviewId] = useState(null);

    const load = () => {
        setLoading(true);
        getToxReviewQueue()
            .then((res) => {
                const data = (res && res.data) || [];
                setRows(data);
                const sel = {};
                data.forEach((r) => { if (r.clean) sel[r.orderId] = true; });
                setSelected(sel);
            })
            .catch(() => toastr.error('Could not load the review queue'))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const shown = useMemo(() => rows.filter((r) => !onlyExceptions || !r.clean), [rows, onlyExceptions]);
    const selIds = rows.filter((r) => selected[r.orderId]).map((r) => r.orderId);
    const allShownSelected = shown.length > 0 && shown.every((r) => selected[r.orderId]);
    const toggleAll = () => { const s = { ...selected }; shown.forEach((r) => { s[r.orderId] = !allShownSelected; }); setSelected(s); };
    const clean = rows.filter((r) => r.clean).length;

    const submit = () => {
        if (!password) { toastr.error('Enter your password to sign'); return; }
        setBusy(true);
        batchToxReview({ orderIds: selIds, action, password })
            .then((res) => {
                if (res && res.success) { setResults(res.data); setAction(null); setPassword(''); load(); }
                else toastr.error((res && res.message) || 'Batch failed');
            })
            .catch(() => toastr.error('Batch failed'))
            .finally(() => setBusy(false));
    };

    return (
        <div className="container-fluid" style={{ padding: 20 }}>
            <h3 style={{ marginBottom: 4 }}>Review Queue</h3>
            <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #dee2e6', margin: '8px 0 12px' }}>
                {[{ key: 'tox', label: 'Toxicology' }].map((t) => (
                    <span key={t.key} style={{ padding: '6px 16px', fontWeight: 600, color: '#0d6efd', borderBottom: '3px solid #0d6efd', marginBottom: -2 }}>{t.label}</span>
                ))}
            </div>
            <p style={{ color: '#6c757d', marginTop: 0 }}>
                Toxicology requisitions with confirmation results waiting for review. Clean results (no inconsistencies, no critical values) are pre-selected; exceptions are highlighted for individual review.
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0' }}>
                <span>{rows.length} waiting</span>
                <span style={pill('#d1e7dd', '#0f5132')}>{clean} clean</span>
                <span style={pill('#f8d7da', '#842029')}>{rows.length - clean} exceptions</span>
                <Form.Check type="switch" id="tox-only-exceptions" label="Show only exceptions" checked={onlyExceptions} onChange={(e) => setOnlyExceptions(e.target.checked)} />
                <Button variant="outline-secondary" size="sm" onClick={load}>Refresh</Button>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {Object.keys(ACTIONS).map((k) => (
                        <Button key={k} variant={ACTIONS[k].variant} size="sm" disabled={!selIds.length} onClick={() => setAction(k)}>
                            {ACTIONS[k].label} ({selIds.length})
                        </Button>
                    ))}
                </div>
            </div>
            <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #dee2e6', borderRadius: 6 }}>
                <table className="table table-sm mb-0" style={{ minWidth: 900 }}>
                    <thead>
                        <tr>
                            <th style={{ width: 36 }}><input type="checkbox" checked={allShownSelected} onChange={toggleAll} /></th>
                            <th>Requisition</th><th>Patient</th><th>Facility</th><th>Collected</th>
                            <th className="text-center">Detected</th><th className="text-center">Inconsistent</th><th className="text-center">Critical</th>
                            <th>Review</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan="10" className="text-center p-4">Loading…</td></tr>}
                        {!loading && !shown.length && <tr><td colSpan="10" className="text-center p-4">Nothing waiting for review.</td></tr>}
                        {!loading && shown.map((r) => (
                            <tr key={r.orderId} style={{ background: r.clean ? undefined : '#fff5f5' }}>
                                <td><input type="checkbox" checked={!!selected[r.orderId]} onChange={(e) => setSelected({ ...selected, [r.orderId]: e.target.checked })} /></td>
                                <td>{r.lab_order_id}</td>
                                <td>{r.patient}</td>
                                <td>{r.facility}</td>
                                <td>{fmtDate(r.collected)}</td>
                                <td className="text-center">{r.detected}/{r.total}</td>
                                <td className="text-center"><span style={r.inconsistent ? pill('#f8d7da', '#842029') : GREY}>{r.inconsistent}</span></td>
                                <td className="text-center">
                                    <span title={r.critical ? (r.callbacks ? 'Callback logged' : 'Callback needed before Final') : ''} style={r.critical ? pill('#fff3cd', '#664d03') : GREY}>
                                        {r.critical}{r.critical && !r.callbacks ? ' !' : ''}
                                    </span>
                                </td>
                                <td>{r.report_status === 'preliminary' ? 'Preliminary' : r.applied ? 'Applied' : 'Not applied'}</td>
                                <td>
                                    <button className="edit-order-btn" title="Open Tox Review" onClick={() => setReviewId(r.orderId)}>
                                        <i className="fas fa-flask" style={{ color: r.clean ? '#198754' : '#dc3545' }}></i>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {reviewId && <ToxReviewPopup orderId={reviewId} onClose={() => setReviewId(null)} onApplied={load} />}
            <Modal show={!!action} onHide={() => !busy && setAction(null)} centered>
                <Modal.Header closeButton><Modal.Title>{action && ACTIONS[action].label}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <p>{action && ACTIONS[action].help}</p>
                    <p><strong>{selIds.length}</strong> requisition(s) selected.</p>
                    <Form.Group>
                        <Form.Label>Password (electronic signature)</Form.Label>
                        <Form.Control type="password" name="esig-password" autoComplete="new-password" value={password} autoFocus onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
                    </Form.Group>
                    <small className="text-muted">By signing you confirm you reviewed these results. Each order records your name, the action and the time.</small>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" disabled={busy} onClick={() => setAction(null)}>Cancel</Button>
                    <Button variant="primary" disabled={busy} onClick={submit}>{busy ? 'Signing…' : 'Sign & Run'}</Button>
                </Modal.Footer>
            </Modal>
            <Modal show={!!results} onHide={() => setResults(null)} centered size="lg">
                <Modal.Header closeButton><Modal.Title>Batch result</Modal.Title></Modal.Header>
                <Modal.Body>
                    {results && (
                        <>
                            <p><strong>{results.done}</strong> done, <strong>{results.skipped}</strong> skipped. Signed by {results.signer}.</p>
                            <table className="table table-sm">
                                <thead><tr><th>Requisition</th><th>Result</th><th>Details</th></tr></thead>
                                <tbody>
                                    {results.results.map((x) => (
                                        <tr key={x.orderId}>
                                            <td>{x.lab_order_id}</td>
                                            <td style={{ color: x.ok ? '#198754' : '#dc3545', fontWeight: 600 }}>{x.ok ? 'Done' : 'Skipped'}</td>
                                            <td>{x.message}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={() => setResults(null)}>Close</Button></Modal.Footer>
            </Modal>
        </div>
    );
}
